import { type MetaFunction } from '@remix-run/node'
import { type Location, type Params } from '@remix-run/react'
import * as R from 'remeda'
import { API_MODE, ORIGIN_WEBSITE } from '~/constants/env'
import {
  i18nextConfig,
  PREFIXED_PATHS_BY_LOCALE,
  type ValuesByLocale
} from '~/i18n/config'
import { parseUnknownLocale } from '~/i18n/i18n.utils'
import { matchIsNodeProduction } from '~/utils/env'

const PREFIX_TITLE = 'Zoī -'
const DEFAULT_TITLE = `${PREFIX_TITLE} Tout commence par votre check-up`
const DEFAULT_DESCRIPTION =
  'Check-up à 360° à Paris et application de suivi, pour optimiser sa qualité de vie, au quotidien et pour longtemps. Prenez le contrôle avec Zoī.'

const HREF_LANGS_BY_LOCALE: ValuesByLocale<string> = {
  fr: 'fr-FR',
  en: 'en-US'
}

const localesPrefixRegExp = R.pipe(
  PREFIXED_PATHS_BY_LOCALE,
  R.values(),
  R.map((lang) => {
    return lang.replace('/', '\\/')
  }),
  R.join('|'),
  (pattern) => {
    return new RegExp(`(${pattern})`)
  }
)

function buildMetasAlternates(location: Location): ReturnType<MetaFunction> {
  const metas: ReturnType<MetaFunction> = []

  R.forEachObj(HREF_LANGS_BY_LOCALE, (hrefLang, key) => {
    const url = new URL(ORIGIN_WEBSITE)
    // replace myurl.com/fr/my-page by myurl.com/en/my-page
    url.pathname = location.pathname.replace(
      localesPrefixRegExp,
      PREFIXED_PATHS_BY_LOCALE[key]
    )

    metas.push({ rel: 'alternate', hrefLang, href: url.toString() })
  })

  return metas
}

type BuilderParam = Partial<{
  location: Location
  title: string
  description: string
  robots: string
  params: Params
}>

export function buildMetas(
  { title, description, robots, params, location }: BuilderParam,
  additionalMetas?: ReturnType<MetaFunction>
): ReturnType<MetaFunction> {
  const parsedLocale = parseUnknownLocale(params?.lang)
  const locale = parsedLocale.data ?? i18nextConfig.fallbackLng
  const titleFormatted = title ? `${PREFIX_TITLE} ${title}` : DEFAULT_TITLE
  const descriptionFormatted = description || DEFAULT_DESCRIPTION
  const originUrlSuffixed = new URL(ORIGIN_WEBSITE)

  if (location) {
    originUrlSuffixed.pathname = location.pathname.replace(
      localesPrefixRegExp,
      PREFIXED_PATHS_BY_LOCALE[locale]
    )
  }

  const isRobotsDisabled = matchIsNodeProduction() && API_MODE !== 'production'

  let metas: ReturnType<MetaFunction> = [
    { title: titleFormatted },
    { name: 'description', content: descriptionFormatted },
    {
      name: 'og:title',
      content: titleFormatted
    },
    {
      name: 'og:description',
      content: descriptionFormatted
    },
    {
      name: 'og:type',
      content: 'website'
    },
    {
      name: 'og:url',
      content: originUrlSuffixed.toString()
    },
    {
      name: 'og:locale',
      content: HREF_LANGS_BY_LOCALE[locale]
    },
    {
      name: 'og:image',
      content: `${ORIGIN_WEBSITE}/zoi.png`
    },
    {
      name: 'og:image:alt',
      content: 'Zoī'
    },
    {
      name: 'og:image:type',
      content: 'image/png'
    },
    {
      name: 'og:image:width',
      content: '1200'
    },
    {
      name: 'og:image:height',
      content: '630'
    },
    {
      name: 'og:site_name',
      content: 'Zoī'
    },
    {
      name: 'twitter:card',
      content: 'summary_large_image'
    },
    {
      name: 'twitter:domain',
      content: 'zoi.com'
    },
    {
      name: 'twitter:url',
      content: originUrlSuffixed.toString()
    },
    {
      name: 'twitter:title',
      content: titleFormatted
    },
    {
      name: 'twitter:description',
      content: descriptionFormatted
    },
    {
      name: 'twitter:image',
      content: `${ORIGIN_WEBSITE}/zoi.png`
    },
    {
      name: 'theme-color',
      content: '#121212'
    },
    {
      name: 'robots',
      content: isRobotsDisabled
        ? 'noindex, nofollow'
        : robots || 'index, follow'
    }
  ]

  if (location) {
    metas = metas.concat(buildMetasAlternates(location))
  }

  if (additionalMetas) {
    metas = metas.concat(additionalMetas)
  }

  return metas
}
